import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Target, Edit, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { cn, getUnitLabel } from '../../lib/utils';
import { SortableContainer, SortableItem } from '../SortableKPI';

// Shared grid template for consistent alignment
const GRID_COLUMNS_EDIT = "36px 44px minmax(200px, 1.5fr) minmax(120px, 0.8fr) minmax(140px, 0.9fr) minmax(80px, 0.5fr) minmax(110px, 0.6fr) minmax(80px, 0.4fr) 44px";
const GRID_COLUMNS_NORMAL = "44px minmax(200px, 1.5fr) minmax(120px, 0.8fr) minmax(140px, 0.9fr) minmax(80px, 0.5fr) minmax(110px, 0.6fr) minmax(80px, 0.4fr) 44px";
const GRID_COLUMNS_MANAGEMENT = "40px 44px minmax(200px, 1.5fr) minmax(120px, 0.8fr) minmax(140px, 0.9fr) minmax(80px, 0.5fr) minmax(110px, 0.6fr) minmax(80px, 0.4fr) 44px";

const KPIListView = ({
  kpis,
  isAdmin,
  isManagementMode,
  selectedKPIs,
  onSelectAll,
  onSelectKPI,
  onKPIClick,
  onEditKPI,
  // Drag & drop props
  isEditOrderMode = false,
  onDragEnd,
  onMoveKpi,
}) => {
  const handleRowClick = (kpi, e) => {
    // Don't navigate if clicking on drag handle, move buttons, or in edit order mode
    if (e.target.closest('[data-drag-handle]') || e.target.closest('[data-move-buttons]')) {
      return;
    }
    if (isEditOrderMode) {
      return; // Block navigation in edit order mode
    }
    if (isManagementMode) {
      onSelectKPI(kpi.id, e);
    } else {
      onKPIClick(kpi);
    }
  };

  const getGridColumns = () => {
    if (isManagementMode) return GRID_COLUMNS_MANAGEMENT;
    if (isEditOrderMode) return GRID_COLUMNS_EDIT;
    return GRID_COLUMNS_NORMAL;
  };

  const renderHeader = () => (
    <div 
      className="grid items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider"
      style={{ gridTemplateColumns: getGridColumns() }}
    >
      {/* Handle column - only in edit mode */}
      {isEditOrderMode && <div></div>}
      
      {/* Checkbox column - only in management mode */}
      {isManagementMode && (
        <div className="flex justify-center">
          <Checkbox
            checked={selectedKPIs.size === kpis.length && kpis.length > 0}
            onCheckedChange={onSelectAll}
          />
        </div>
      )}
      
      {/* Icon column */}
      <div></div>
      
      {/* KPI Name */}
      <div>KPI</div>
      
      {/* Area */}
      <div className="hidden md:block">Área</div>
      
      {/* Responsible */}
      <div className="hidden lg:block">Responsable</div>
      
      {/* Unit */}
      <div className="hidden xl:block">Unidad</div>
      
      {/* Evaluation Type */}
      <div className="hidden sm:block">Tipo</div>
      
      {/* Status */}
      <div>Estado</div>
      
      {/* Actions */}
      <div></div>
    </div>
  );

  const renderRow = (kpi, index, dragProps = {}) => {
    const { attributes, listeners, isDragging } = dragProps;
    
    return (
      <div
        className={cn(
          "grid items-center gap-2 px-4 py-3 border-b border-slate-100 transition-colors",
          !isEditOrderMode && !isManagementMode && "cursor-pointer hover:bg-slate-50",
          isManagementMode && selectedKPIs.has(kpi.id) && "bg-emerald-50",
          isDragging && "bg-blue-50 shadow-lg opacity-90 z-50",
          isEditOrderMode && "cursor-default"
        )}
        style={{ 
          gridTemplateColumns: getGridColumns(),
          minHeight: '64px'
        }}
        onClick={(e) => handleRowClick(kpi, e)}
        data-testid={`kpi-row-${kpi.id}`}
      >
        {/* Handle column - only in edit mode */}
        {isEditOrderMode && (
          <div className="flex justify-center" data-drag-handle>
            <button
              type="button"
              className="touch-none cursor-grab active:cursor-grabbing p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-4 h-4" />
            </button>
          </div>
        )}
        
        {/* Checkbox column - only in management mode */}
        {isManagementMode && (
          <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedKPIs.has(kpi.id)}
              onCheckedChange={() => {
                onSelectKPI(kpi.id, { stopPropagation: () => {} });
              }}
              data-testid={`kpi-row-checkbox-${kpi.id}`}
            />
          </div>
        )}
        
        {/* Icon */}
        <div className="flex justify-center">
          <div className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
            kpi.is_active ? 'bg-emerald-50' : 'bg-slate-100'
          )}>
            <Target className={cn('w-4 h-4', kpi.is_active ? 'text-emerald-600' : 'text-slate-400')} />
          </div>
        </div>
        
        {/* KPI Name */}
        <div className="min-w-0">
          <p 
            className="font-medium text-slate-900 truncate uppercase text-sm" 
            title={kpi.name}
          >
            {kpi.name}
          </p>
          {/* Show area on mobile */}
          <p className="text-xs text-slate-500 truncate uppercase md:hidden">{kpi.area}</p>
        </div>
        
        {/* Area - hidden on mobile */}
        <div className="hidden md:block">
          <p className="text-slate-600 text-sm truncate uppercase">{kpi.area}</p>
        </div>
        
        {/* Responsible - hidden on small */}
        <div className="hidden lg:block">
          <p className="text-slate-600 text-sm truncate">{kpi.responsible_name}</p>
        </div>
        
        {/* Unit - hidden on medium */}
        <div className="hidden xl:block">
          <p className="text-slate-600 text-sm">{getUnitLabel(kpi.unit)}</p>
        </div>
        
        {/* Evaluation Type Badge - hidden on mobile */}
        <div className="hidden sm:flex">
          <Badge 
            variant="outline" 
            className="text-xs whitespace-nowrap inline-flex items-center px-2 py-0.5"
          >
            {kpi.evaluation_type === 'percentage' ? 'Mayor mejor' : 'Menor mejor'}
          </Badge>
        </div>
        
        {/* Status Badge */}
        <div>
          <Badge 
            variant={kpi.is_active ? 'default' : 'secondary'} 
            className={cn(
              'text-xs whitespace-nowrap inline-flex items-center px-2 py-0.5',
              kpi.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''
            )}
          >
            {kpi.is_active ? 'Activo' : 'Inactivo'}
          </Badge>
        </div>
        
        {/* Actions */}
        <div className="flex justify-center">
          {isAdmin && !isEditOrderMode && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                onEditKPI(kpi, e);
              }}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderMobileRow = (kpi, index) => (
    <div
      key={kpi.id}
      className={cn(
        "p-4 border-b border-slate-100",
        !isEditOrderMode && !isManagementMode && "cursor-pointer hover:bg-slate-50 active:bg-slate-100",
        isManagementMode && selectedKPIs.has(kpi.id) && "bg-emerald-50"
      )}
      onClick={(e) => handleRowClick(kpi, e)}
      data-testid={`kpi-mobile-${kpi.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Mobile move buttons - only in edit mode */}
        {isEditOrderMode && (
          <div className="flex flex-col gap-0" data-move-buttons>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-400"
              onClick={(e) => { e.stopPropagation(); onMoveKpi?.(kpi.id, 'up'); }}
              disabled={index === 0}
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-slate-400"
              onClick={(e) => { e.stopPropagation(); onMoveKpi?.(kpi.id, 'down'); }}
              disabled={index === kpis.length - 1}
            >
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* Checkbox - management mode */}
        {isManagementMode && (
          <Checkbox
            checked={selectedKPIs.has(kpi.id)}
            onCheckedChange={() => {
              onSelectKPI(kpi.id, { stopPropagation: () => {} });
            }}
            onClick={(e) => e.stopPropagation()}
            className="flex-shrink-0"
          />
        )}
        
        {/* Icon */}
        <div className={cn(
          'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
          kpi.is_active ? 'bg-emerald-50' : 'bg-slate-100'
        )}>
          <Target className={cn('w-5 h-5', kpi.is_active ? 'text-emerald-600' : 'text-slate-400')} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-medium text-slate-900 truncate uppercase text-sm" title={kpi.name}>
              {kpi.name}
            </h3>
            <Badge 
              variant={kpi.is_active ? 'default' : 'secondary'} 
              className={cn(
                'text-xs flex-shrink-0 whitespace-nowrap',
                kpi.is_active ? 'bg-emerald-50 text-emerald-700' : ''
              )}
            >
              {kpi.is_active ? 'Activo' : 'Inactivo'}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            <span className="uppercase truncate">{kpi.area}</span>
            <span className="text-slate-300">•</span>
            <span className="truncate">{kpi.responsible_name}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card data-testid="kpis-list-view">
      <CardContent className="p-0">
        {/* Desktop Table - sm and up */}
        <div className="hidden sm:block">
          {/* Header */}
          {renderHeader()}
          
          {/* Body with optional drag & drop */}
          {isEditOrderMode && onDragEnd ? (
            <SortableContainer
              items={kpis}
              onDragEnd={onDragEnd}
              strategy="vertical"
              disabled={false}
            >
              <div>
                {kpis.map((kpi, index) => (
                  <SortableItem key={kpi.id} id={kpi.id} disabled={false}>
                    {(dragProps) => renderRow(kpi, index, dragProps)}
                  </SortableItem>
                ))}
              </div>
            </SortableContainer>
          ) : (
            <div>
              {kpis.map((kpi, index) => renderRow(kpi, index))}
            </div>
          )}
        </div>
        
        {/* Mobile List - below sm */}
        <div className="block sm:hidden">
          {kpis.map((kpi, index) => renderMobileRow(kpi, index))}
        </div>
      </CardContent>
    </Card>
  );
};

export default KPIListView;
